import React, { Component, Fragment } from 'react';
import { withRouter } from 'react-router';

import { ButtonIcon } from 'components/button';
import { I18n } from 'components/translate';
import { ROUTES } from 'configs/routes/constants';
import Settings from 'configs/Settings';
import { withShellContext } from 'hoc/withShellContext';
import { inject, observer } from 'mobx-react';
import { FeatureToggles } from 'modules/featureToggle';
import { MembershipService, MembershipStore } from 'modules/membership';
import { PlayerService } from 'modules/player';
import SubtitleActions from 'modules/subtitle/SubtitleActions';
import PropTypes from 'prop-types';

import SubtitleItem from './SubtitleItem';
import SubtitleTranscribeBanner from './SubtitleTranscribeBanner';
import SubtitleTranslateBanner from './SubtitleTranslateBanner';
import SubtitleTranslateModal from './SubtitleTranslateModal';
import SubtitleUploadModal from './SubtitleUploadModal';
import SubtitleUploadTranscribe from './SubtitleUploadTranscribe';

import {
  SUBTITLE_STATUS,
  SUBTITLE_TYPE,
  POOLING_INTERVAL
} from '../../constants';

import './SubtitleSection.scss';

@withRouter
@inject((stores) => ({
  subtitles: stores.SubtitleStore.subtitles,
  isCreateModuleConceptEnabled: stores.FeatureToggleStore.featureToggles.get(
    FeatureToggles.CreateModuleConcept
  )
}))
@withShellContext
@observer
export default class SubtitleSection extends Component {
  state = {
    selectedSubtitle: null,
    translateSubtitle: null,
    uploadModal: false
  };

  static propTypes = {
    subtitles: PropTypes.array,
    mediaId: PropTypes.string,
    originalLanguage: PropTypes.string,
    videoName: PropTypes.string,
    setOriginalLanguage: PropTypes.func,
    history: PropTypes.object,
    location: PropTypes.object,
    isMembership: PropTypes.bool
  };

  get mediaId() {
    return this.props.mediaId;
  }

  get subtitles() {
    return this.props.subtitles[this.mediaId] || [];
  }

  get transcript() {
    return this.subtitles.find(
      (subtitle) => SUBTITLE_TYPE.TRANSCRIPTION === subtitle.type
    );
  }

  get isAnySubtitleProcessing() {
    return this.subtitles.find(
      (subtitle) => SUBTITLE_STATUS.PROCESSING === subtitle.status
    );
  }

  get isManagementEnabled() {
    return this.subtitles.length > 0;
  }

  get isTranscribeEnabled() {
    return this.subtitles.length === 0;
  }

  get isTranscribeOriginalLanguage() {
    const { originalLanguage } = this.props;

    return this.isTranscribeEnabled && originalLanguage;
  }

  get isTranslateBannerEnabled() {
    const isTranscriptEnabled =
      this.transcript &&
      this.transcript.status !== SUBTITLE_STATUS.PROCESSING &&
      this.transcript.status !== SUBTITLE_STATUS.ERROR;

    return this.subtitles.length === 1 && isTranscriptEnabled;
  }

  componentDidMount() {
    SubtitleActions.setSubtitleList(this.mediaId);
    SubtitleActions.setSupportedTranscriptionLangs();

    PlayerService.setSupportedLangs();
  }

  componentDidUpdate() {
    const shouldStartPooling =
      this.isAnySubtitleProcessing && !this.poolingInterval;

    shouldStartPooling && this.poolingStatus();
  }

  componentWillUnmount() {
    this.resetPolling();
  }

  render() {
    return (
      <div className="subtitle__section">
        <div className="subtitle__section__header">
          <I18n label="player_subtitles.title" />
        </div>

        <div className="subtitle__section__body">
          {this.isManagementEnabled && this.renderManagement()}
          {this.renderUpload()}
        </div>
      </div>
    );
  }

  renderTranslationModal() {
    const { translateSubtitle } = this.state;
    const translatedCodes = (this.subtitles || []).map(
      (subtitle) => subtitle.languageCode
    );

    return (
      <SubtitleTranslateModal
        mediaId={this.mediaId}
        subtitleId={translateSubtitle}
        onClose={this.onCloseTranslate}
        translatedCodes={translatedCodes}
      />
    );
  }

  renderUpload() {
    return (
      <Fragment>
        {this.state.uploadModal && (
          <SubtitleUploadModal
            onClose={this.closeUpload}
            mediaId={this.mediaId}
          />
        )}
        {this.isTranscribeEnabled && this.renderTranscribe()}

        {!this.isTranscribeOriginalLanguage && (
          <ButtonIcon
            className="subtitle__section__upload mt__sm"
            onClick={this.showUpload}
            icon="upload"
            transparent
          >
            <I18n label="player_subtitles.upload.label" />
          </ButtonIcon>
        )}
      </Fragment>
    );
  }

  renderTranscribe() {
    if (this.isTranscribeOriginalLanguage) {
      return (
        <SubtitleUploadTranscribe
          onUpload={this.showUpload}
          mediaId={this.mediaId}
        />
      );
    }

    return (
      <SubtitleTranscribeBanner
        setOriginalLanguage={this.props.setOriginalLanguage}
        mediaId={this.mediaId}
      />
    );
  }

  renderManagement() {
    const { translateSubtitle } = this.state;

    return (
      <div className="subtitle__management">
        <ul className="subtitle__list">
          {this.subtitles.map(this.renderSubtitle)}

          {this.isTranslateBannerEnabled && (
            <SubtitleTranslateBanner
              onTranslate={this.onClickTranslate}
              mediaId={this.mediaId}
              subtitleId={this.transcript.id}
            />
          )}
        </ul>

        {translateSubtitle && this.renderTranslationModal()}
      </div>
    );
  }

  renderSubtitle = (subtitle) => (
    <SubtitleItem
      key={subtitle.id}
      mediaId={this.mediaId}
      onReview={this.goToReviewSubtitle}
      onTranslate={this.onClickTranslate}
      videoName={this.props.videoName}
      {...subtitle}
    />
  );

  poolingStatus() {
    this.poolingInterval = setInterval(() => {
      !this.isAnySubtitleProcessing && this.resetPolling();
      SubtitleActions.setSubtitleList(this.mediaId);
    }, POOLING_INTERVAL);
  }

  resetPolling = () => {
    clearInterval(this.poolingInterval);
    this.poolingInterval = null;
  };

  goToReviewSubtitle = (selectedSubtitle) => {
    const { history, location, isCreateModuleConceptEnabled, isMembership } =
      this.props;

    this.setState({ selectedSubtitle });

    const reviewSubtitle = ROUTES.CONTENT.REVIEW_SUBTITLE.replace(
      ':mediaId',
      this.mediaId
    ).replace(':subtitleId', selectedSubtitle);

    if (isMembership && isCreateModuleConceptEnabled) {
      const url = `${Settings.app.bw}/membership/${MembershipService.slug}/products/edit/${MembershipStore.productId}/admin/beta${reviewSubtitle}`;

      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }

    history.push({ pathname: reviewSubtitle, search: location.search });
  };

  showUpload = () => this.setState({ uploadModal: true });
  closeUpload = () => this.setState({ uploadModal: false });

  onClickTranslate = (id) => this.setState({ translateSubtitle: id });
  onCloseTranslate = () => this.setState({ translateSubtitle: null });
}
